import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import MUITable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import SearchIcon from '@mui/icons-material/Search';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import classNames from 'classnames';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';
import { useApiRequest } from '~/hooks/api';
import {
  DictionaryCustomFieldType,
  ICustomDictionaryItem,
  ICustomDictionaryType,
} from '~/services/api/dictionaries/types';

import Loader from '~/ui/components/common/Loader';
import Input from '~/ui/components/inputs/Input';

import TableBodyItem from './components/TableBodyItem';

import styles from '~/ui/pages/Dictionaries/Dictionaries.module.scss';

const defaultValues = {
  name: '',
};

interface IProps {
  dictionaryType: ICustomDictionaryType;
  hasCustomFields: boolean;
  clinicId?: number;
  tableClassName?: string;
  stickyHeader?: boolean;
  setDictionaryItem: (item: ICustomDictionaryItem | null) => void;
  onEditDictionaryItem: (item: ICustomDictionaryItem) => void;
}

const Table = ({
  dictionaryType,
  hasCustomFields,
  clinicId,
  tableClassName,
  stickyHeader,
  setDictionaryItem,
  onEditDictionaryItem,
}: IProps): ReactElement => {
  const formMethods = useForm({ defaultValues });
  const [bootstrapLoading, setBootstrapLoading] = useState(false);

  const currentDictionaryType = useRef<string | null>(null);

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = formMethods;

  const list = useStoreState(state => state.dictionaries.list);
  const pagination = useStoreState(state => state.dictionaries.pagination);
  const onGetTypeList = useStoreActions(actions => actions.dictionaries.onGetTypeList);
  const showError = useStoreActions(actions => actions.snackbar.showError);

  const name = watch('name');

  const normalizedPagination = useMemo(
    () => ({
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
    }),
    [pagination.pageNumber, pagination.pageSize],
  );

  const { isLoading: isSearchLoading, refetch } = useApiRequest(
    () =>
      onGetTypeList({
        type: dictionaryType.type,
        params: { name, ...normalizedPagination, pageNumber: 1 },
        clinicId,
      }),
    { debounceMs: 350, dependencies: [name] },
  );

  useEffect(() => {
    const run = async () => {
      try {
        setBootstrapLoading(true);
        const isTypeChange = currentDictionaryType.current !== dictionaryType.type;
        currentDictionaryType.current = dictionaryType.type;
        await onGetTypeList({
          type: dictionaryType.type,
          params: {
            name,
            ...normalizedPagination,
            ...(isTypeChange ? { pageNumber: 1 } : {}),
          },
          clinicId,
        });
      } catch (e) {
        showError(extractErrorMessage(e));
      } finally {
        setBootstrapLoading(false);
      }
    };
    run();
    // Intentionally omit `name` / `normalizedPagination`: only react to dictionary context; search uses debounced hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictionaryType.type, clinicId]);

  const onSubmitImmediate = () => {
    refetch();
  };

  const handlePagination = (pageSize: number, pageNumber: number) => {
    onGetTypeList({ type: dictionaryType.type, params: { name, pageSize, pageNumber }, clinicId });
  };

  const loading = bootstrapLoading || isSearchLoading;

  const textColumnsCount =
    1 +
    (hasCustomFields
      ? dictionaryType.customFields.filter(
          ({ type }) => type !== DictionaryCustomFieldType.CHECKBOX,
        ).length
      : 0);
  const textColumnStyle = { width: `${100 / textColumnsCount}%` };

  const headerCustomFields = hasCustomFields
    ? dictionaryType.customFields.map(({ title, type }) => {
        const isCheckbox = type === DictionaryCustomFieldType.CHECKBOX;
        return (
          <TableCell
            key={title}
            className={classNames(styles.headCell, { [styles.centeredCell]: isCheckbox })}
            style={isCheckbox ? undefined : textColumnStyle}
          >
            {title}
          </TableCell>
        );
      })
    : null;

  return (
    <TableContainer className={tableClassName}>
      <Box sx={{ p: 2 }}>
        {loading ? <Loader className={styles.loader} /> : null}
        <Grid container spacing={1} className={styles.tableHeader}>
          <Grid
            size={{
              sm: 4,
            }}
          >
            <h3 className={styles.cardTitle}>{dictionaryType?.name}</h3>
          </Grid>
          <Grid
            size={{
              sm: 8,
            }}
          >
            <FormProvider {...formMethods}>
              <form onSubmit={handleSubmit(onSubmitImmediate)} className={styles.searchWrapper}>
                <Input
                  startAdornment={<SearchIcon color="primary" />}
                  placeholder={`Search ${dictionaryType?.name}`}
                  register={register}
                  name="name"
                  errors={errors}
                />
              </form>
            </FormProvider>
          </Grid>
        </Grid>
      </Box>

      <MUITable stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            <TableCell className={styles.headCell} style={textColumnStyle}>
              {dictionaryType?.name}
            </TableCell>
            {hasCustomFields && headerCustomFields}
            <TableCell className={styles.headCell}>Status</TableCell>
            <TableCell className={classNames(styles.headCell, styles.actionsCell)}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map(item => (
            <TableBodyItem
              key={item.id}
              dictionaryItem={item}
              setDictionaryItem={setDictionaryItem}
              dictionaryType={dictionaryType}
              hasCustomFields={hasCustomFields}
              onEditDictionaryItem={onEditDictionaryItem}
            />
          ))}
        </TableBody>
      </MUITable>
      <TablePagination
        component="div"
        count={pagination.total}
        onPageChange={(_, pageNumber) => handlePagination(pagination.pageSize, pageNumber + 1)}
        onRowsPerPageChange={e => handlePagination(Number(e.target.value), 1)}
        page={pagination.pageNumber - 1}
        rowsPerPage={pagination.pageSize}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </TableContainer>
  );
};

export default Table;
